import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Api from "src/Config";
import { createContext, FunctionComponent, ReactElement, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { textTruncate } from "src/helpers/truncateText";
import { format } from "date-fns";
import { Text } from "@chakra-ui/react";

export const EditTrailContext = createContext(null);

type Props = {
  children: ReactElement;
};

const score_color: any = ["#05CD99", "#F8961E", "#F94144"];

const EditTrailProvider: FunctionComponent<Props> = ({ children }) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const pageQuery = queryParams.get("page") || 1;

  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const tabQuery = queryParams.get("tab");

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["edit_trail", pageQuery],
    staleTime: 30000,
    enabled: tabQuery === "score-configuration",
    queryFn: async () => {
      const url = `settings/onboarding/threshold/audit?page_size=10&page=${decodeURIComponent(
        queryParams as any,
      )}`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          user: {
            first_name: string;
            last_name: string;
          };
          threshold_changes: Array<string>;
          threshold_edited: Array<string>;
          created_at: string;
        }) => ({
          id: res.id,
          editor: `${
            !res.user.first_name?.toLowerCase() &&
            !res.user.last_name?.toLowerCase()
              ? "N/A"
              : textTruncate(
                  `${res.user.first_name?.toLowerCase() || ""} ${
                    res.user.last_name?.toLowerCase() || ""
                  }`,
                  20,
                )
          }`,
          "threshold edited":
            res.threshold_edited.map((threshold: string, index: number) => (
              <Text color={score_color[index]}>
                {threshold.replace("/", " / ")}
              </Text>
            )) || "N/A",
          "date edited": format(new Date(res.created_at), "dd/MM/yyyy"),
          "old/new threshold":
            res.threshold_changes.map((threshold: string, index: number) => (
              <Text color={score_color[index]}>{threshold}</Text>
            )) || "N/A",
        }),
      );

      return {
        response,
        metadata: data?.metadata,
      };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const handleSearch = (e: any) => {
    setSearchParam(e.target.value);
  };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      handleSearch,
      searchParam,
      isFetching,
      // handlePaginate,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      error,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      // handlePaginate,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
    ],
  );
  return (
    <EditTrailContext.Provider value={contextValue}>
      {children}
    </EditTrailContext.Provider>
  );
};

export default EditTrailProvider;
